import { useCallback, useContext, useState } from "react";
import { CustomTypeTemplate } from "../../api/CustomTypes/CustomTypeTemplates";
import { Button } from "../../common/buttons/Button/Button";
import { EntityDetailProps, EntityDetailTableProps } from "../../common/entity/EntityInterfaces";
import { NotSet } from "../../common/misc/UIconstants";
import { Table } from "../../common/panels/Detail/DetailTable";
import { DetailsPageTopControlsView } from "../../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";
import { CustomTypeTemplateTile } from "../../common/sidebar/tiles/CustomTypeTemplateTile";
import { EntityTypeToDetailPageTemplatePreview } from "../CustomTypeTemplates/CustomTypeTemplateManager";
import { SessionContext } from "../../common/contexts/SessionContext";
import { usePostMutation } from "../../api/BaseEntityApi";
import { useHistory } from "react-router-dom";
import { customTypeConstants } from "../../api/CustomTypes/CustomTypes";
import { getIndexRoute } from "../../main/Routing";
import { AlertModal } from "../../common/modals/AlertModal/AlertModal";
import { LucideIcon } from "../../common/icon/LucideIcon";

export const CustomTypeTemplatePackagesDetailTable = ({
  entity,
  entityConstants,
  fieldLabels,
}: EntityDetailTableProps<"customTypeTemplatePackages">) => {
  const [currentCustomTypeTemplate, setCurrentCustomTypeTemplate] = useState<CustomTypeTemplate>();
  return (
    <>
      <Table noPadding nonFoldable>
        <Table.Head>Details</Table.Head>
        <Table.Body>
          <Table.Body.RowContent
            title={fieldLabels.name}
            content={
              <div className="container_label">
                <div className="container_label_name" style={{ fontWeight: 600 }}>
                  {entity.name ?? NotSet}
                </div>
                <div className="container_label_id">
                  <span style={{ whiteSpace: "nowrap" }}>Package-ID: {entity.id}</span>
                </div>
              </div>
            }
          />

          <Table.Body.RowContent
            title={fieldLabels.description}
            content={
              <>
                {entity.description ? (
                  <div className="container_notes">
                    <textarea rows={4} className="container_textarea" value={entity?.description} disabled />
                  </div>
                ) : (
                  NotSet
                )}
              </>
            }
          />
        </Table.Body>
      </Table>

      <Table noPadding nonFoldable>
        <Table.Head>Custom types contained:</Table.Head>
        <Table.Body>
          <div style={{ padding: "10px" }}>
            <CustomTypeTemplateTile
              headless
              defaultFilters={{ customTypeTemplatePackageIds: [entity.id] }}
              excludeFilters={{ customTypeTemplatePackageIds: () => false }}
              controlsUnfolded={() => null}
              onRowClick={(e) => setCurrentCustomTypeTemplate((prevState) => (prevState?.id === e.id ? undefined : e))}
              rowControls={(row) => (
                <div
                  className="label label-info"
                  style={{ visibility: !!row.customTypesFromCurrentTemplate?.length ? "visible" : "hidden" }}
                >
                  In use
                </div>
              )}
              rowStyle={(row) =>
                row.id === currentCustomTypeTemplate?.id
                  ? { border: "1px dashed var(--primary)", backgroundColor: "var(--primary-light)" }
                  : undefined
              }
            />
          </div>
        </Table.Body>
      </Table>

      {!!currentCustomTypeTemplate && (
        <EntityTypeToDetailPageTemplatePreview
          customTypeTemplate={currentCustomTypeTemplate}
          entityType={currentCustomTypeTemplate.entityType}
        />
      )}
    </>
  );
};

export const CustomTypeTemplatePackagesDetail = ({
  permissions,
  routes,
  entity,
  entityApi,
  entityConstants,
  fieldLabels,
  entityTypeId,
}: EntityDetailProps<"customTypeTemplatePackages">) => {
  const { session, route } = useContext(SessionContext);
  const history = useHistory();

  var { mutateAsync } = usePostMutation<any, null>({
    resource: `${entityConstants.resource}/${entity.id}/create` as any,
  });
  const create = useCallback(async () => {
    await mutateAsync(
      { body: null },
      {
        onSuccess(data, variables, context) {
          history.push(route(getIndexRoute(customTypeConstants.frontendIndexRoute)));
        },
      }
    ).catch((e) => console.error(e));
  }, [history, mutateAsync, route]);

  const [showModal, setShowModal] = useState<boolean>(false);
  return (
    <div style={{ backgroundColor: "var(--white)", padding: "10px", height: "100%", overflow: "auto" }}>
      <DetailsPageTopControlsView
        entityTypeId={entityTypeId}
        entity={entity}
        entityConstants={entityConstants}
        controls={
          <Button
            disabled={!session?.permissions.administer_custom_field_schemas}
            className="btn btn-primary flex align-center gap-5"
            onClick={() => setShowModal(true)}
          >
            <LucideIcon name="plus" />
            Create types from package
          </Button>
        }
      />
      <div style={{ padding: "10px 0" }}>
        <CustomTypeTemplatePackagesDetailTable
          entityTypeId={entityTypeId}
          entity={entity}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels}
        />
      </div>
      <AlertModal
        showModal={showModal}
        setShowModal={setShowModal}
        onProceed={() => create()}
        type="primary"
        title="Confirm creation"
        description="Proceeding will create all custom types contained in this package. Are you sure you want to proceed?"
        proceedLabel="Confirm"
      />
    </div>
  );
};
